<template>
  <div>
    <div class="card">
      <div class="card-body">
        <h4 class="card-title">SEPA Sammler</h4>
        <p class="card-description">
          Einzelne pain.008-XML-Dateien hochladen — Backend prüft und kombiniert sie zu
          einer Sammel-Lastschrift-XML. Es muss derselbe Gläubiger in allen Dateien sein.
        </p>

        <div
          class="sepa-dropzone"
          :class="{ 'sepa-dropzone--active': isDragOver }"
          @click="openFilePicker"
          @dragenter.prevent="onDragEnter"
          @dragover.prevent="onDragOver"
          @dragleave.prevent="onDragLeave"
          @drop.prevent="onDrop"
        >
          <input
            ref="fileInput"
            type="file"
            multiple
            accept=".xml,text/xml,application/xml"
            style="display: none"
            @change="onInputChange"
          />
          <div class="sepa-dropzone__icon">
            <i class="mdi mdi-cloud-upload mdi-48px"></i>
          </div>
          <div class="sepa-dropzone__text">
            <strong>XML-Dateien hier rein ziehen</strong>
            <br />
            oder klicken zum Auswählen
          </div>
        </div>

        <b-table
          v-if="entries.length"
          class="mt-3"
          small
          striped
          :items="entries"
          :fields="fields"
        >
          <template #cell(amount)="row">
            {{ row.item.amount != null ? formatEur(row.item.amount) : '–' }}
          </template>
          <template #cell(status)="row">
            <span v-if="row.item.status === 'ok'" class="text-success">OK</span>
            <span v-else-if="row.item.status === 'loading'" class="text-muted">…</span>
            <span v-else class="text-danger" :title="row.item.error">Fehler</span>
          </template>
          <template #cell(actions)="row">
            <b-button size="sm" variant="outline-danger" @click="removeEntry(row.index)">
              Entfernen
            </b-button>
          </template>
        </b-table>

        <div v-if="entries.length" class="mt-3">
          <strong>{{ okCount }} Position(en)</strong> — Summe: {{ formatEur(totalAmount) }}
          <div v-if="hasErrors" class="text-danger mt-2">
            Es gibt fehlerhafte Dateien. Bitte entfernen, bevor du eine Sammel-XML erzeugst.
          </div>
        </div>

        <div class="mt-3">
          <b-button
            variant="success"
            :disabled="!canMerge || merging"
            @click="merge"
          >
            {{ merging ? 'Erzeuge…' : 'Sammel-XML herunterladen' }}
          </b-button>
          <b-button v-if="entries.length" variant="link" @click="clearAll">
            Liste leeren
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { parseSepaXml, mergeSepaXml } from '@/services/sepa-merger.service';

export default {
  name: 'sepa-merger',
  data() {
    return {
      entries: [],
      merging: false,
      isDragOver: false,
      dragCounter: 0,
      fields: [
        { key: 'filename', label: 'Dateiname' },
        { key: 'invoice_nr', label: 'Rechnungsnr.' },
        { key: 'debtor_name', label: 'Kunde' },
        { key: 'debtor_iban', label: 'IBAN' },
        { key: 'amount', label: 'Betrag' },
        { key: 'status', label: 'Status' },
        { key: 'actions', label: '' },
      ],
    };
  },
  computed: {
    okCount() {
      return this.entries.filter((e) => e.status === 'ok').length;
    },
    hasErrors() {
      return this.entries.some((e) => e.status === 'error');
    },
    totalAmount() {
      return this.entries
        .filter((e) => e.status === 'ok' && e.amount != null)
        .reduce((acc, e) => acc + e.amount, 0);
    },
    canMerge() {
      return (
        this.entries.length > 0 &&
        !this.hasErrors &&
        this.entries.every((e) => e.status === 'ok')
      );
    },
  },
  methods: {
    formatEur(value) {
      if (typeof value !== 'number') return value;
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
      }).format(value);
    },
    openFilePicker() {
      this.$refs.fileInput.click();
    },
    onInputChange(event) {
      this.addFiles(event.target.files);
      this.$refs.fileInput.value = null;
    },
    onDragEnter() {
      this.dragCounter += 1;
      this.isDragOver = true;
    },
    onDragOver() {
      this.isDragOver = true;
    },
    onDragLeave() {
      this.dragCounter -= 1;
      if (this.dragCounter <= 0) {
        this.dragCounter = 0;
        this.isDragOver = false;
      }
    },
    onDrop(event) {
      this.isDragOver = false;
      this.dragCounter = 0;
      this.addFiles(event.dataTransfer.files);
    },
    addFiles(fileList) {
      if (!fileList || !fileList.length) return;
      const files = Array.from(fileList).filter((f) => /\.xml$/i.test(f.name));
      if (!files.length) return;
      for (const file of files) {
        const entry = {
          filename: file.name,
          file,
          status: 'loading',
          invoice_nr: '',
          debtor_name: '',
          debtor_iban: '',
          amount: null,
          error: '',
        };
        this.entries.push(entry);
        this.parseOne(entry);
      }
    },
    parseOne(entry) {
      parseSepaXml(entry.file)
        .then((resp) => {
          if (resp.status === 'error') {
            entry.status = 'error';
            entry.error = resp.message || 'Fehler';
          } else {
            const d = resp.data || {};
            entry.status = 'ok';
            entry.invoice_nr = d.invoice_nr;
            entry.debtor_name = d.debtor_name;
            entry.debtor_iban = d.debtor_iban;
            entry.amount = d.amount;
          }
        })
        .catch((err) => {
          entry.status = 'error';
          entry.error =
            (err && err.response && err.response.data && err.response.data.message) ||
            'Upload fehlgeschlagen';
        });
    },
    removeEntry(index) {
      this.entries.splice(index, 1);
    },
    clearAll() {
      this.entries = [];
    },
    merge() {
      this.merging = true;
      const files = this.entries
        .filter((e) => e.status === 'ok')
        .map((e) => e.file);
      mergeSepaXml(files)
        .catch((err) => {
          const msg =
            (err && err.response && err.response.data && err.response.data.message) ||
            'Sammel-XML konnte nicht erzeugt werden';
          if (this.$bvToast) {
            this.$bvToast.toast(msg, { title: 'Fehler', variant: 'danger' });
          } else {
            alert(msg);
          }
        })
        .finally(() => {
          this.merging = false;
        });
    },
  },
};
</script>

<style scoped>
.sepa-dropzone {
  border: 2px dashed #b0bec5;
  border-radius: 8px;
  padding: 2.5rem 1rem;
  text-align: center;
  cursor: pointer;
  background-color: #fafafa;
  transition: all 0.15s ease-in-out;
  user-select: none;
}
.sepa-dropzone:hover {
  border-color: #2563eb;
  background-color: #f5f8ff;
}
.sepa-dropzone--active {
  border-color: #2563eb;
  background-color: #eff6ff;
}
.sepa-dropzone__icon {
  color: #90a4ae;
  margin-bottom: 0.5rem;
}
.sepa-dropzone--active .sepa-dropzone__icon {
  color: #2563eb;
}
.sepa-dropzone__text {
  color: #546e7a;
  font-size: 0.95rem;
  line-height: 1.4;
}
</style>
